import { useState, useCallback } from 'react';
import { AboutModal, buildAboutConfig } from '@ascend/about-modal';
import appIdentity from 'virtual:app-identity';
import { AppShell } from './components/layout/AppShell';
import { FeedbackForm, FeedbackButton } from './components/FeedbackForm/FeedbackForm';
import { FeedbackContext } from './components/common/feedbackContext';
import { AboutContext } from './components/common/aboutContext';
import type { UserFeedback } from './utils/userFeedback';

const aboutConfig = buildAboutConfig(appIdentity, 'VITE_APP_');

/**
 * App — root of the unlocked experience.
 *
 * feedback-widget integration (Stage 6h): the floating FeedbackButton is
 * always present, and the FeedbackForm modal is conditionally opened. The
 * `handleOpenFeedback` signature is the one an ErrorBoundary fallback
 * should call, e.g. `handleOpenFeedback('bug_report', 'bug', err.message)`.
 *
 * Hooks are called unconditionally at the top level per React's rules.
 */
export default function App() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackConfig, setFeedbackConfig] = useState<{
    initialType?: UserFeedback['type'];
    initialCategory?: string;
    initialError?: string;
  }>({});
  const [aboutOpen, setAboutOpen] = useState(false);

  const handleOpenFeedback = useCallback(
    (
      type?: UserFeedback['type'],
      category?: string,
      error?: string,
    ) => {
      const next: {
        initialType?: UserFeedback['type'];
        initialCategory?: string;
        initialError?: string;
      } = {};
      if (type !== undefined) next.initialType = type;
      if (category !== undefined) next.initialCategory = category;
      if (error !== undefined) next.initialError = error;
      setFeedbackConfig(next);
      setFeedbackOpen(true);
    },
    [],
  );

  const handleCloseFeedback = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackConfig({});
  }, []);

  const handleOpenAbout = useCallback(() => setAboutOpen(true), []);
  const handleCloseAbout = useCallback(() => setAboutOpen(false), []);

  return (
    <FeedbackContext.Provider value={handleOpenFeedback}>
      <AboutContext.Provider value={handleOpenAbout}>
        <AppShell />

        <FeedbackForm
          isOpen={feedbackOpen}
          onClose={handleCloseFeedback}
          {...feedbackConfig}
        />
        <FeedbackButton onOpen={handleOpenFeedback} />
        <AboutModal
          isOpen={aboutOpen}
          onClose={handleCloseAbout}
          config={aboutConfig}
        />
      </AboutContext.Provider>
    </FeedbackContext.Provider>
  );
}
